import clsx from 'clsx'
import React, { useEffect, useRef, useState } from 'react'
import { flushSync } from 'react-dom'

import { trackTileSliderInteraction } from '@Analytics/tileInteractions'
import AngleLeftSVG from '@Assets/SVG/AngleLeft'
import AngleRightSVG from '@Assets/SVG/AngleRight'
import { ImageV3 } from '@Components/Image'

import styles from './AssetsSlider.module.scss'

// shared by every tile image spot; any preload image must use the exact
// same value so the browser picks (and caches) the same srcset candidate
export const TILE_IMAGE_SIZES = '(max-width: 767px) 50vw, (max-width: 1199px) 33vw, (max-width: 1999px) 25vw, 20vw'

// the compact candidate list corresponding to TILE_IMAGE_SIZES: covers
// 50vw mobile … 20vw desktop across 1x–3x displays with five entries,
// where the default config's expansion emits ~11 — that srcset text is
// most of a grouped grid's per-tile markup. Shared with the grouped tile's
// sibling warm-up, which must mirror the slide's exact attrs so the warmed
// URL is the one the swapped-in slide requests.
export const TILE_IMAGE_DEVICE_SIZES = [384, 640, 828, 1080, 1536]

// the slide a scroller rests on (or is nearest to, mid-scroll). Slides are
// pitched at the tile width + 2px (see .assets in the module scss), so the
// pitch is measured instead of assumed to be clientWidth, which drifts 2px
// per slide
export const currentSlide = (container: HTMLElement): number => {
  const count = container.children.length
  if (!count || !container.scrollWidth) {
    return 0
  }
  return Math.round(container.scrollLeft / (container.scrollWidth / count))
}

// the scroll offset a slide settles at: exactly where its snap alignment
// (start for the first, end for the last, centered otherwise) rests it.
//
// The arrows navigate by absolute offset, never by scrollBy(): the slides are
// 2px wider than the scrollport, and a snap area larger than its scrollport
// is a valid snap position anywhere it covers the port — a 2px band per slide
// instead of a single point. A relative scroll is resolved in the travel
// direction with scroll-snap-stop: always honoured, and that pair trips over
// the bands: scrollBy(clientWidth) lands on the NEAR edge of the next slide's
// band (closer to the requested offset than the slide's centre), and the
// click after it is captured by the snap-stop rule, which halts at the first
// aligned snap position ahead — the centre of the slide already showing, 1px
// further in. Every other click nudged the image by a pixel instead of
// turning the page. An absolute scrollTo() is resolved against the requested
// offset alone, so a target inside the next slide's band lands there.
export const slideRestingOffset = (container: HTMLElement, index: number): number => {
  const count = container.children.length
  if (index <= 0 || !count) {
    return 0
  }
  if (index >= count - 1) {
    return container.scrollWidth - container.clientWidth
  }
  // every slide is the same fractional width, laid out without gaps, so a
  // slide starts at a multiple of that width. Measured on the first slide
  // rather than read from the target's offsetLeft (whole pixels) or rect
  // (translated while the first tile's scroll hint plays)
  const slideWidth = (container.children[0] as HTMLElement).getBoundingClientRect().width
  const overflow = slideWidth - container.getBoundingClientRect().width
  return index * slideWidth + overflow / 2
}

interface AssetsSliderProps {
  assetURLs: string[]
  // extra class for the scroller root — the tiles pass their own module's
  // hook here so tile-scoped sibling selectors (the out-of-stock grayscale)
  // can still reach the slider's images
  className?: string
  // render the images with the trimmed TILE_IMAGE_DEVICE_SIZES candidate
  // list, cutting the per-image srcset markup (and the priority images'
  // head-preload text)
  compactSrcSet?: boolean
  // a grouped-tile swap changes the displayed product in place; the id
  // change is what rewinds the slider (static tiles never trigger it)
  id: string
  onFirstImageLoad?: () => void
  priority?: boolean
  // extra class per slide, for tile-specific slide styling (the grouped
  // tile drives the slide padding by container width instead of viewport)
  slideClassName?: string
  tileIndex?: number
  title: string
}

export const AssetsSlider = ({
  assetURLs,
  className,
  compactSrcSet = false,
  id,
  onFirstImageLoad,
  priority = true,
  slideClassName,
  tileIndex = 0,
  title,
}: AssetsSliderProps) => {
  const assetsRef = useRef<HTMLDivElement>(null)
  const [interacted, setInteracted] = useState(false)
  const [activeSlide, setActiveSlide] = useState(0)
  const previousID = useRef(id)
  // undefined keeps ImageV3 on the global candidate expansion (control);
  // the list switches opted-in callers to the trimmed srcset
  const slideDeviceSizes = compactSrcSet ? TILE_IMAGE_DEVICE_SIZES : undefined
  // set while the swap effect rewinds the slider programmatically: that
  // rewind fires onScroll, which must not count as user interaction
  const suppressScroll = useRef(false)

  // the slide an arrow click is scrolling toward while that smooth scroll is
  // in flight, so a quick second click chains from the destination instead of
  // from a mid-animation scrollLeft (which rounds to whichever slide is nearer
  // and would swallow the click). Cleared once the scroller reaches it, or as
  // soon as a scroll moves away from it (the user took over)
  const pendingSlide = useRef<number | null>(null)
  const lastScrollLeft = useRef(0)

  const scrollToSlide = (direction: 1 | -1) => (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault()
    event.stopPropagation()
    const container = assetsRef.current
    if (!container) {
      return
    }
    // the first tile's scroll hint translates the slides while it plays, and
    // the snap target is resolved against the translated snap areas when
    // scrollTo() is called — a click mid-hint parked the slider a third of a
    // slide short. The first scroll event cancels the hint anyway (interacted);
    // do it now, synchronously, so the slides are at rest when the target is
    // measured and resolved
    flushSync(() => setInteracted(true))
    const last = container.children.length - 1
    const from = pendingSlide.current ?? currentSlide(container)
    const target = Math.min(last, Math.max(0, from + direction))
    pendingSlide.current = target
    container.scrollTo({ behavior: 'smooth', left: slideRestingOffset(container, target) })
  }

  // a grouped-tile swap changes the displayed product in place (no remount:
  // that would re-enter the slider arrows at opacity 0 and replay their
  // hover fade-in on every selection) — so rewind the slider state manually.
  // The previousID guard keeps the initial mount from counting as a swap.
  useEffect(() => {
    if (previousID.current === id) {
      return
    }

    previousID.current = id
    setActiveSlide(0)
    // interacted deliberately survives the swap: it's the user's knowledge
    // of THIS tile's slider, not a property of the displayed product — and
    // clearing it would lift the .interacted animation cancel, restarting
    // the scroll hint on the persistent slides
    pendingSlide.current = null
    lastScrollLeft.current = 0
    // only flag when a scroll event will actually fire (scrollLeft changes),
    // or the next genuine user scroll would be swallowed
    if (assetsRef.current && assetsRef.current.scrollLeft !== 0) {
      suppressScroll.current = true
      assetsRef.current.scrollLeft = 0
    }
  }, [id])

  return (
    <>
      <div
        className={clsx(styles.assets, interacted && styles.interacted, className)}
        onScroll={(event) => {
          if (suppressScroll.current) {
            suppressScroll.current = false
            return
          }

          setInteracted(true)
          const container = event.currentTarget
          const { scrollLeft } = container
          const slide = currentSlide(container)
          if (pendingSlide.current !== null) {
            const destination = slideRestingOffset(container, pendingSlide.current)
            const receding = Math.abs(scrollLeft - destination) > Math.abs(lastScrollLeft.current - destination)
            if (slide === pendingSlide.current || receding) {
              pendingSlide.current = null
            }
          }
          lastScrollLeft.current = scrollLeft
          if (slide !== activeSlide) {
            // counted once per slide change regardless of input (swipe,
            // trackpad or arrows); folded into page_exited. Only list tiles
            // carry a 1-based tileIndex — callers without one (e.g. the
            // recommendation grids) must not pollute the aggregates with
            // index-0 events
            if (tileIndex) {
              trackTileSliderInteraction(tileIndex)
            }
            setActiveSlide(slide)
          }
        }}
        ref={assetsRef}
      >
        <div className={clsx(styles.slide, slideClassName)}>
          <ImageV3
            alt={title}
            className={styles.image}
            decoding="async"
            deviceSizes={slideDeviceSizes}
            draggable={false}
            fetchPriority={priority ? 'high' : undefined}
            fill
            loading={priority ? 'eager' : 'lazy'}
            onLoad={onFirstImageLoad}
            priority={priority}
            sizes={TILE_IMAGE_SIZES}
            src={assetURLs[0]}
            style={{
              objectFit: 'contain',
            }}
            title={title}
          />
        </div>

        {assetURLs.slice(1).map((asset, index) => (
          // eslint-disable-next-line react/no-array-index-key
          <div key={index} className={clsx(styles.slide, slideClassName)}>
            {/* the second slide always renders (the scroll hint reveals it);
                the rest only load once the slider has been interacted with —
                browsers prefetch lazy images in scrollers too eagerly to
                rely on loading="lazy" alone.
                keyed by POSITION, not asset url: a grouped swap then reuses
                the slide elements (only src changes), so the first-view
                scroll-hint animation doesn't restart on freshly-mounted
                slides — it replays only via the wrapper's in-view attribute
                toggle (scroll out and back) or a real remount.
                no .image class: fill's position: absolute must survive so the
                image escapes the slide padding and renders to the edge.
                alt is empty on purpose: inside the tile link these are
                decorative repeats of the product the link already names */}
            {(index === 0 || interacted) && (
              <ImageV3
                alt=""
                decoding="async"
                deviceSizes={slideDeviceSizes}
                draggable={false}
                fetchPriority="low"
                fill
                loading="lazy"
                overrideQuality="50,65"
                sizes={TILE_IMAGE_SIZES}
                src={asset}
                style={{
                  objectFit: 'cover',
                }}
              />
            )}
          </div>
        ))}
      </div>

      <div className={styles.dots}>
        {assetURLs.map((asset, index) => (
          <span key={asset} className={clsx(styles.dot, index === activeSlide && styles.activeDot)} />
        ))}
      </div>

      <button
        aria-label="Previous"
        className={clsx(styles.sliderArrow, styles.left)}
        disabled={activeSlide === 0}
        onClick={scrollToSlide(-1)}
        tabIndex={-1}
        type="button"
      >
        <AngleLeftSVG />
      </button>

      <button
        aria-label="Next"
        className={clsx(styles.sliderArrow, styles.right)}
        disabled={activeSlide >= assetURLs.length - 1}
        onClick={scrollToSlide(1)}
        tabIndex={-1}
        type="button"
      >
        <AngleRightSVG />
      </button>
    </>
  )
}
